import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import './AdminDashboard.css';

const PLACEHOLDER = 'https://via.placeholder.com/80x56?text=—';
const PER_PAGE = 25;

const STATUS_OPTIONS = [
  { label: 'For Sale / Active', value: 'For Sale,Active' },
  { label: 'All statuses',      value: '' },
  { label: 'Pending',           value: 'Pending' },
  { label: 'Sold',              value: 'Sold' },
  { label: 'Off Market',        value: 'Off Market' },
  { label: 'Pending Review',    value: 'Pending Admin Review' },
];

// Column -> server sort field. null = not sortable.
const SORT_FIELDS = {
  address:  'address',
  agent:    'listedBy.agentName',
  price:    'numericPrice',
  bd:       'bedrooms',
  ba:       'bathrooms',
  likes:    'likeCount',
  dislikes: 'dislikeCount',
  swipes:   'swipeCount',
  status:   'propertyStatus',
};

const STATUS_COLORS = {
  'For Sale':             { bg: '#ecfdf5', fg: '#047857' },
  'Active':               { bg: '#ecfdf5', fg: '#047857' },
  'Pending':              { bg: '#fffbeb', fg: '#b45309' },
  'Pending Admin Review': { bg: '#fffbeb', fg: '#b45309' },
  'Sold':                 { bg: '#fef2f2', fg: '#b91c1c' },
  'Off Market':           { bg: '#f3f4f6', fg: '#4b5563' },
};
const statusStyle = (s) => STATUS_COLORS[s] || { bg: '#eff6ff', fg: '#1d4ed8' };

// Listing boost lengths (must match ListingBoost.DURATIONS_HOURS on the backend)
const BOOST_OPTIONS = [
  { hours: 24,  label: '24h' },
  { hours: 72,  label: '72h' },
  { hours: 168, label: '1 wk' },
];
const boostTimeLeft = (endsAt) => {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return 'Ending…';
  const h = Math.floor(ms / 3600000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.round(ms / 60000))}m left`;
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '—';
  if (typeof price === 'number') return `$${price.toLocaleString()}`;
  const s = String(price).trim();
  return s.startsWith('$') ? s : `$${s}`;
};
const neighborhoodName = (n) => (typeof n === 'string' ? n : (n && n.name) || ''); // eslint-disable-line no-unused-vars
const agentName = (p) => {
  const lb = p.listedBy || {};
  const a = p.agent && typeof p.agent === 'object' ? p.agent : null;
  return lb.agentName || a?.name || '—';
};

const AgentListingsTab = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('For Sale,Active');
  const [sortCol, setSortCol] = useState(null);   // key of SORT_FIELDS
  const [sortDir, setSortDir] = useState('desc');  // 'asc' | 'desc'
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');        // debounced

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [boosts, setBoosts] = useState({});           // propertyId -> live boost
  const [boostPickerFor, setBoostPickerFor] = useState(null); // propertyId choosing a length
  const [boostBusyFor, setBoostBusyFor] = useState(null);     // propertyId mid-request
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Refetch on any query change
  useEffect(() => { fetchPage(page); }, [page, statusFilter, sortCol, sortDir, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPage = async (p) => {
    setLoading(true);
    setError('');
    try {
      const filters = { agentListed: true, withSwipeStats: true, page: p, limit: PER_PAGE };
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.search = search;
      if (sortCol && SORT_FIELDS[sortCol]) {
        filters.sort = (sortDir === 'desc' ? '-' : '') + SORT_FIELDS[sortCol];
      }
      const res = await propertyService.getProperties(filters);
      const data = Array.isArray(res) ? res : (res?.data || []);
      setRows(data);
      setTotal(res && typeof res.total === 'number' ? res.total : null);
      loadBoosts(data.map(r => r._id || r.id).filter(Boolean));
    } catch (e) {
      console.error('AgentListingsTab: fetchPage error', e);
      setError('Could not load agent listings. Check your connection and try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Boost state for the rows on screen. A failure here only hides the toggles'
  // state — it never blocks the listings themselves.
  const loadBoosts = async (ids) => {
    if (ids.length === 0) { setBoosts({}); return; }
    try {
      const res = await propertyService.getListingBoosts(ids);
      const map = {};
      (res?.data || []).forEach(b => { map[String(b.property)] = b; });
      setBoosts(map);
    } catch (e) {
      console.error('AgentListingsTab: loadBoosts error', e);
    }
  };

  const startBoost = async (propertyId, hours) => {
    setBoostBusyFor(propertyId);
    try {
      const res = await propertyService.boostListing(propertyId, hours);
      setBoosts(prev => ({ ...prev, [propertyId]: res.data }));
      setBoostPickerFor(null);
    } catch (e) {
      alert(`Could not boost listing: ${e.message}`);
    } finally {
      setBoostBusyFor(null);
    }
  };

  const endBoost = async (p) => {
    const propertyId = p._id || p.id;
    if (!window.confirm(`End the boost on ${p.address || 'this listing'}?`)) return;
    setBoostBusyFor(propertyId);
    try {
      await propertyService.endListingBoost(propertyId);
      setBoosts(prev => { const next = { ...prev }; delete next[propertyId]; return next; });
    } catch (e) {
      alert(`Could not end boost: ${e.message}`);
    } finally {
      setBoostBusyFor(null);
    }
  };

  const onStatusChange = (v) => { setStatusFilter(v); setPage(1); };
  const onSort = (col) => {
    if (!SORT_FIELDS[col]) return;
    if (sortCol === col) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  const openDetails = (property) => { setSelectedProperty(property); setShowDetailsModal(true); };
  const closeDetails = () => { setShowDetailsModal(false); setSelectedProperty(null); };

  const totalPages = total !== null ? Math.max(1, Math.ceil(total / PER_PAGE)) : null;
  const canPrev = page > 1;
  const canNext = totalPages !== null ? page < totalPages : rows.length === PER_PAGE;

  const card = {
    background: '#ffffff', border: '1px solid #f0f0f0', borderRadius: '14px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  };
  const baseTh = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    color: '#9ca3af', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap',
    userSelect: 'none',
  };
  const td = {
    fontSize: '13px', color: '#111', padding: '10px 14px',
    borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle',
  };
  const tdNum = { ...td, textAlign: 'left', fontWeight: 700 };
  const pageBtn = (enabled) => ({
    padding: '8px 16px', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
    border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff',
    color: enabled ? '#2563eb' : '#d1d5db', cursor: enabled ? 'pointer' : 'default',
  });

  // Sortable header cell
  const Th = ({ col, label, divider = false }) => {
    const sortable = !!SORT_FIELDS[col];
    const active = sortCol === col;
    return (
      <th
        onClick={() => sortable && onSort(col)}
        style={{ ...baseTh, textAlign: 'left', cursor: sortable ? 'pointer' : 'default', color: active ? '#2563eb' : '#9ca3af', ...(divider ? { borderLeft: '1px solid #f0f0f0' } : {}) }}
        title={sortable ? 'Sort' : undefined}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {label}
          {sortable && (
            <span style={{ fontSize: '9px', lineHeight: 1, color: active ? '#2563eb' : '#d1d5db' }}>
              {active ? (sortDir === 'desc' ? '▼' : '▲') : '↕'}
            </span>
          )}
        </span>
      </th>
    );
  };

  return (
    <div style={{ color: '#111' }}>
      {/* Branded header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>Properties</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by agent or address…"
            style={{
              flex: '1 1 240px', minWidth: '200px', maxWidth: '360px',
              padding: '9px 12px', fontSize: '13px', fontFamily: 'inherit', color: '#111',
              border: '1px solid #e5e7eb', borderRadius: '10px', outline: 'none', background: '#fff',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
          />
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            style={{
              padding: '9px 12px', fontSize: '13px', fontFamily: 'inherit', color: '#111',
              border: '1px solid #e5e7eb', borderRadius: '10px', outline: 'none', background: '#fff', cursor: 'pointer',
            }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.label} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {error ? (
        <div style={{ ...card, padding: '32px', textAlign: 'center', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
      ) : (
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '920px' }}>
              <thead>
                <tr>
                  <th style={baseTh}></th>
                  <Th col="address" label="Address" />
                  <Th col="agent" label="Agent" />
                  <Th col="price" label="Price" />
                  <Th col="bd" label="Bd" />
                  <Th col="ba" label="Ba" />
                  <Th col="likes" label="Likes" divider />
                  <Th col="dislikes" label="Dislikes" />
                  <Th col="swipes" label="Swipes" />
                  <Th col="status" label="Status" />
                  <th style={{ ...baseTh, textAlign: 'left' }}>Boost</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: '40px' }} colSpan={11}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: '40px' }} colSpan={11}>No agent listings found.</td></tr>
                ) : (
                  rows.map((p, idx) => {
                    const st = statusStyle(p.propertyStatus);
                    const csz = p.csz || '';
                    const pid = p._id || p.id;
                    const boost = pid ? boosts[pid] : null;
                    const boostBusy = boostBusyFor === pid;
                    // Agent-paid boosts always run their full length
                    const boostLocked = boost?.source === 'agent';
                    return (
                      <tr
                        key={p._id || p.id || idx}
                        onClick={() => openDetails(p)}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ ...td, width: '72px' }}>
                          <img
                            src={p.mainImage || PLACEHOLDER}
                            alt=""
                            style={{ width: '56px', height: '40px', objectFit: 'cover', borderRadius: '6px', display: 'block', background: '#f3f4f6' }}
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                          />
                        </td>
                        <td style={{ ...td, minWidth: '220px', textAlign: 'left' }}>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>
                            {p.address || 'No address'}
                          </div>
                          {csz && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>{csz}</div>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'left' }}>{agentName(p)}</td>
                        <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPrice(p.price)}</td>
                        <td style={{ ...tdNum, fontWeight: 400 }}>{p.bedrooms ?? '—'}</td>
                        <td style={{ ...tdNum, fontWeight: 400 }}>{p.bathrooms ?? '—'}</td>
                        <td style={{ ...tdNum, color: '#047857', borderLeft: '1px solid #f0f0f0' }}>{p.likeCount ?? 0}</td>
                        <td style={{ ...tdNum, color: '#b91c1c' }}>{p.dislikeCount ?? 0}</td>
                        <td style={{ ...tdNum, color: '#111' }}>{p.swipeCount ?? 0}</td>
                        <td style={td}>
                          {p.propertyStatus && (
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', background: st.bg, color: st.fg, whiteSpace: 'nowrap' }}>
                              {p.propertyStatus}
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {boostPickerFor === pid && !boost ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {BOOST_OPTIONS.map(o => (
                                <button
                                  key={o.hours}
                                  disabled={boostBusy}
                                  onClick={() => startBoost(pid, o.hours)}
                                  style={{
                                    padding: '4px 8px', fontSize: '11px', fontWeight: 700, fontFamily: 'inherit',
                                    border: '1px solid #bfdbfe', borderRadius: '6px', background: '#eff6ff',
                                    color: '#1d4ed8', cursor: boostBusy ? 'default' : 'pointer', opacity: boostBusy ? 0.5 : 1,
                                  }}
                                >
                                  {o.label}
                                </button>
                              ))}
                              <button
                                onClick={() => setBoostPickerFor(null)}
                                title="Cancel"
                                style={{ padding: '4px 6px', fontSize: '12px', border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <button
                                role="switch"
                                aria-checked={!!boost}
                                disabled={!pid || boostBusy || boostLocked}
                                onClick={() => (boost ? endBoost(p) : setBoostPickerFor(pid))}
                                title={boostLocked ? 'Paid by the agent — runs until it ends' : boost ? 'End boost' : 'Boost this listing'}
                                style={{
                                  position: 'relative', width: '34px', height: '20px', padding: 0, flexShrink: 0,
                                  border: 'none', borderRadius: '10px', background: boost ? '#2563eb' : '#e5e7eb',
                                  cursor: boostBusy || boostLocked ? 'default' : 'pointer', opacity: boostBusy ? 0.5 : 1,
                                  transition: 'background 0.15s',
                                }}
                              >
                                <span style={{
                                  position: 'absolute', top: '2px', left: boost ? '16px' : '2px',
                                  width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                                }} />
                              </button>
                              {boost && (
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8' }}>
                                  {boostLocked ? 'Paid · ' : ''}{boostTimeLeft(boost.endsAt)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px', borderTop: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              {total !== null ? `${total.toLocaleString()} listings` : ''}
              {totalPages !== null ? ` · Page ${page} of ${totalPages}` : ''}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={pageBtn(canPrev && !loading)} disabled={!canPrev || loading} onClick={() => canPrev && setPage(page - 1)}>← Prev</button>
              <button style={pageBtn(canNext && !loading)} disabled={!canNext || loading} onClick={() => canNext && setPage(page + 1)}>Next →</button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedProperty && (
        <PropertyDetailsModal
          property={selectedProperty}
          isOpen={showDetailsModal}
          onClose={closeDetails}
        />
      )}
    </div>
  );
};

export default AgentListingsTab;